import {inject} from '@angular/core';
import {
  ApiActionsWithRequestAndSuccessPayload,
  TLabels,
  TranslateHelper,
} from '@binotel-frontend/bnt-common-components';
import {
  ESubjectSyncStatuses,
  ESubjectType,
  ISubjectFavoritesStatus,
  ISyncStatusProgress,
} from '@entities/subject';
import {Actions, ofType} from '@ngrx/effects';
import {Action, ActionCreator, Store} from '@ngrx/store';
import {ErrorsHelper, SnackbarHelper} from '@shared/helper';
import {OdApiErrorResponse} from '@shared/services';
import {
  createFunctionalEffect,
  createFunctionalSideEffect,
} from '@shared/utils/effects.helper';
import {
  catchError,
  interval,
  last,
  map,
  Observable,
  of,
  switchMap,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs';

import {SubjectApi} from './subject.api';

type FavoriteAction = ActionCreator<
  string,
  (props: {payload: ISubjectFavoritesStatus}) => {
    payload: ISubjectFavoritesStatus;
  } & Action<string>
>;

type PlainAction = ActionCreator<string, () => Action<string>>;

type SyncActions = ApiActionsWithRequestAndSuccessPayload<
  {subjectId: number; code: string | null; subjectType: ESubjectType},
  {payload: ISyncStatusProgress}
>;

interface IFavoriteToggleConfig {
  trigger: FavoriteAction;
  failed: PlainAction;
  request: (api: SubjectApi, subjectId: number) => Observable<unknown>;
  successMessage: TLabels;
}

interface ISyncConfig {
  syncActions: SyncActions;
  clearAction: PlainAction;
  pollIntervalMs: number;
}

export const createFavoriteToggleEffect = ({
  trigger,
  failed,
  request,
  successMessage,
}: IFavoriteToggleConfig): ReturnType<typeof createFunctionalSideEffect> =>
  createFunctionalSideEffect(
    (
      actions$ = inject(Actions),
      api = inject(SubjectApi),
      store = inject(Store)
    ) =>
      actions$.pipe(
        ofType(trigger),
        switchMap(({payload}) =>
          request(api, payload.subjectId).pipe(
            tap(() =>
              SnackbarHelper.open(TranslateHelper.translate(successMessage))
            ),
            catchError(() => {
              store.dispatch(failed());
              return of(null);
            })
          )
        )
      )
  );

export const createSyncEffect = ({
  syncActions,
  clearAction,
  pollIntervalMs,
}: ISyncConfig): ReturnType<typeof createFunctionalEffect> =>
  createFunctionalEffect(
    (actions$ = inject(Actions), api = inject(SubjectApi)) => {
      const syncFailed = (error: OdApiErrorResponse) =>
        of(
          syncActions.failed({
            error: ErrorsHelper.getErrorLabelsFromIApi(error),
          })
        );

      return actions$.pipe(
        ofType(syncActions.requested),
        switchMap(({subjectId, code, subjectType}) =>
          api.startSync(subjectId).pipe(
            switchMap(() =>
              interval(pollIntervalMs).pipe(
                switchMap(() => api.checkSync(subjectId)),
                map((response) => response.data),
                takeWhile(
                  (syncStatus) =>
                    syncStatus.syncStatus === ESubjectSyncStatuses.Syncing ||
                    syncStatus.syncStatus === ESubjectSyncStatuses.Failed,
                  true
                ),
                last(),
                map((payload) =>
                  syncActions.succeeded({
                    payload: {...payload, code, subjectType},
                  })
                ),
                catchError(syncFailed)
              )
            ),
            catchError(syncFailed),
            takeUntil(actions$.pipe(ofType(clearAction)))
          )
        )
      );
    }
  );
